import {
  Component,
  ChangeDetectionStrategy,
  computed,
  inject,
  OnInit,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { flatMap, countBy, orderBy, map } from "lodash-es";

import { MeetdayModalFacade } from "../meetday-modal.facade";

interface CompanyStats {
  rank: number;
  companyId: string;
  companyName: string;
  registrationCount: number;
  percentage: number;
}

@Component({
  selector: "app-meetday-tab-registrations",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="btn-toolbar mb-3">
      <h3 class="mb-0">{{ 'Registrations' | translate }}</h3>
      <button class="btn btn-primary ml-auto" (click)="exportStudents()">
        <i class="fa fa-download"></i> {{ 'Export students' | translate }}
      </button>
    </div>

    <div class="mb-3">
      @if (!facade.entity().companiesPerStudent) {
      <div class="alert alert-info">
        <strong>{{ 'Company selection disabled' | translate }}</strong><br>
        {{ 'Students can register without selecting companies' | translate }}
      </div>
      }

      @if (registeredStudents().length > 0) {
      <div class="mb-2">
        <strong>{{ 'Total registered students' | translate }}:</strong> {{ registeredStudents().length }}
      </div>
      }
    </div>

    <!-- Company Analytics Table -->
    @if (showCompanyAnalytics()) {
    <div>
      <h4>{{ 'Company Popularity Rankings' | translate }}</h4>
      <table class="w-100 table table-striped">
        <thead>
          <tr>
            <th>{{ 'Rank' | translate }}</th>
            <th>{{ 'Company Name' | translate }}</th>
            <th>{{ 'Registrations' | translate }}</th>
            <th>{{ 'Percentage' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (stat of companyAnalytics(); track stat.companyId) {
          <tr>
            <td>
              <span class="badge badge-primary">#{{ stat.rank }}</span>
            </td>
            <td>{{ stat.companyName }}</td>
            <td>{{ stat.registrationCount }}</td>
            <td>{{ stat.percentage }}%</td>
          </tr>
          }
        </tbody>
      </table>
    </div>
    }

    <!-- No Data Messages -->
    @if (registeredStudents().length === 0) {
    <div class="alert alert-warning">
      {{ 'No students have registered for this meetday yet' | translate }}
    </div>
    }

    @if (facade.entity().companiesPerStudent && !showCompanyAnalytics() && registeredStudents().length > 0) {
    <div class="alert alert-info">
      {{ 'Students have registered but no company selections have been made yet' | translate }}
    </div>
    }
  `,
})
export class MeetdayTabRegistrationsComponent implements OnInit {
  facade = inject(MeetdayModalFacade);

  // Expose facade's students signal
  registeredStudents = this.facade.registeredStudents;

  // Local computed - calculate company analytics
  companyAnalytics = computed(() => this.calculateCompanyAnalytics());
  showCompanyAnalytics = computed(() => this.companyAnalytics().length > 0);

  ngOnInit(): void {
    const meetDayId = this.facade.entity().id;
    if (meetDayId) {
      this.facade.loadRegisteredStudents(meetDayId);
    }
  }

  exportStudents(): void {
    const meetDayId = this.facade.entity().id;
    if (meetDayId) {
      this.facade.exportStudents(meetDayId);
    }
  }

  private calculateCompanyAnalytics(): CompanyStats[] {
    const students = this.registeredStudents();
    const entity = this.facade.entity();

    if (!students.length || !entity.registeredCompanies) {
      return [];
    }

    // Flatten all chosen companies and count by ID
    const allChosenCompanies = flatMap(students, (s) => s.chosenCompanies || []);
    const companyCounts = countBy(allChosenCompanies, (c) => c.id);

    const totalCompanyStudents = students.filter(
      (s) => s.chosenCompanies && s.chosenCompanies.length > 0
    ).length;

    // Build analytics array from counts
    const analytics = map(companyCounts, (count, companyId) => {
      const company = entity.registeredCompanies?.find((c) => c.id === companyId);
      return {
        rank: 0,
        companyId,
        companyName: company?.name || "",
        registrationCount: count,
        percentage: Math.round((count / totalCompanyStudents) * 100),
      };
    }).filter((stat) => stat.companyName); // Only include companies found in registeredCompanies

    // Sort by registration count descending and assign ranks
    const sortedAnalytics = orderBy(analytics, ["registrationCount"], ["desc"]);
    sortedAnalytics.forEach((item, index) => {
      item.rank = index + 1;
    });

    return sortedAnalytics;
  }
}
